import {
    Module,
    Objective,
    VisibilityStatus,
} from "@evidenceb/gameplay-interfaces";
import React, { useContext, useRef } from "react";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { getResourceIndex } from "../../../utils/dataRetrieval";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { sessionStore } from "../../../contexts/SessionContext";
import Card from "../../../design-system-components/Card/Card";

import { parse } from "@evidenceb/athena-common/modules/HTMLParser";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import ObjectiveListItem from "./ObjectiveListItem";
import { Filter } from "../../../design-system-components/ResourceFilter/Components/filterTypes";
import { filterLearningSets } from "../../SoloAIModuleList/utils";
import { useSafeMappingTree } from "../../../hooks/useMappingTree";
import {
    Accordion,
    AccordionHeader,
    AccordionPanel,
} from "@evidenceb/athena-common/design-system/Accordion";

import useTheme from "../../../hooks/useTheme";
import "./ModuleAccordion.scss";
import Chip from "../../../design-system-components/Chip/Chip";
import ModuleMapLink from "../../../design-system-components/ModuleMapLink/ModuleMapLink";
import { Subject } from "../../../interfaces/User";
import { useData } from "../../../hooks/useData";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";

interface ModuleListItemProps {
    module: Module;
    title: string;
    description: string;
    objectives: Objective[];
    open: boolean;
    filters: Filter[];
    onToggle: (id: string) => void;
    activeSubject: Subject;
}

const ModuleAccordion = ({
    module,
    title,
    description,
    objectives,
    open,
    onToggle,
    filters,
    activeSubject,
}: ModuleListItemProps) => {
    const { session } = useContext(sessionStore);
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const {
        id,
        /* title: moduletitle, */
        studentPath: moduleMapPath,
        visibilityStatus,
    } = module;
    const handleModuleClick =
        visibilityStatus === VisibilityStatus.Visible ||
        visibilityStatus === VisibilityStatus.Unavailable
            ? () => {
                  onToggle(id);
                  ref.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                  });
              }
            : undefined;

    const specimenUnavailable =
        session.specimen && visibilityStatus === VisibilityStatus.Unavailable;

    const mappingTree = useSafeMappingTree();
    const filteredObjectives = mappingTree
        ? filterLearningSets(objectives, filters, mappingTree)
        : objectives;
    const objectivesFilteredAway = objectives.filter((obj) =>
        filteredObjectives.every((filteredObj) => filteredObj.id !== obj.id)
    );

    return (
        <Card
            className={cn("accordion-module-list-item", {
                opened: open,
                closed: !open,
            })}
        >
            <div
                ref={ref}
                role="button"
                className="accordion-module-list-item__module"
                onClick={handleModuleClick}
                onKeyDown={(e) => {
                    if (
                        (e.key === " " || e.key === "Enter") &&
                        handleModuleClick
                    ) {
                        e.preventDefault();
                        handleModuleClick();
                    }
                }}
                tabIndex={0}
                id={`module-${id}`}
                aria-controls={`module-objectives-${id}`}
                aria-expanded={open}
            >
                <div className="icon-and-title">
                    <Icon
                        path="icon_mode_solo_ai"
                        size="clickable"
                        style={
                            specimenUnavailable
                                ? { color: "var(--element-teacher-disabled" }
                                : { color: "var(--element-teacher-default)" }
                        }
                    />
                    <div className="module-list-item__module__title">
                        <h2>
                            {`${title} `}
                            {specimenUnavailable && (
                                <Chip
                                    icon={{ path: "lock" }}
                                    input={intl.formatMessage(
                                        messages["unavailable"]
                                    )}
                                    colorMode={theme}
                                ></Chip>
                            )}
                        </h2>
                        {filters.length === 0 ? (
                            <p>{parse(description)}</p>
                        ) : (
                            <p>
                                {intl.formatMessage(
                                    messages.nbFilteredResults,
                                    {
                                        count: filteredObjectives.length,
                                    }
                                )}
                            </p>
                        )}
                        {moduleMapPath && (
                            <ModuleMapLink
                                moduleMapPath={moduleMapPath}
                                activeSubject={activeSubject}
                                isTeacher={true}
                            />
                        )}
                    </div>
                </div>
                <div className="button-wrapper">
                    {/* <CacheButton
                        className="cache-button-module-teacher"
                        id={id}
                        name={moduletitle.short ?? moduletitle.long}
                    /> */}
                    <Icon
                        role="button"
                        path={`chevron_${open ? "up" : "down"}`}
                        size="large"
                        color="var(--text-main-default)"
                        className="chevron_icon"
                        tabIndex={0}
                        onClick={(e: any) => {
                            if (e && e.currentTarget) {
                                e.currentTarget
                                    .closest(
                                        ".accordion-module-list-item__module"
                                    )
                                    .click();
                            }
                        }}
                        onKeyDown={(e: any) => {
                            if (
                                e &&
                                e.currentTarget &&
                                (e.key === " " || e.key === "Enter")
                            ) {
                                e.currentTarget
                                    .closest(
                                        ".accordion-module-list-item__module"
                                    )
                                    .click();
                            }
                        }}
                    />
                    <VisuallyHidden as="span">
                        {intl.formatMessage(
                            messages.a11yUnfoldModuleObjectives,
                            {
                                moduleTitle: title,
                            }
                        )}
                    </VisuallyHidden>
                </div>
            </div>

            <ObjectivesList objectives={filteredObjectives} moduleId={id} />

            {objectivesFilteredAway.length > 0 && (
                <Accordion className="module-objectives-filtered-away">
                    {(accordionProps) => (
                        <>
                            <AccordionHeader
                                container="h3"
                                {...accordionProps}
                                className={cn(
                                    "className" in accordionProps &&
                                        typeof accordionProps.className ===
                                            "string" &&
                                        accordionProps.className,
                                    "module-objectives-filtered-away__header"
                                )}
                                toggleButton={
                                    <ToggleButton
                                        expanded={accordionProps.expanded}
                                    />
                                }
                            >
                                {intl.formatMessage(messages.nbObjNotMatching, {
                                    count: objectivesFilteredAway.length,
                                })}
                            </AccordionHeader>
                            <AccordionPanel {...accordionProps}>
                                <ObjectivesList
                                    objectives={objectivesFilteredAway}
                                    moduleId={id}
                                />
                            </AccordionPanel>
                        </>
                    )}
                </Accordion>
            )}
        </Card>
    );
};
export default ModuleAccordion;

const ObjectivesList = ({
    objectives,
    moduleId,
}: {
    objectives: Objective[];
    moduleId: string;
}) => {
    const data = useData();
    const disableIndexingByCode = useFeatureFlag("disableIndexingByCode");

    return (
        <ul
            className="objectives-list"
            role="region"
            id={`module-objectives-${moduleId}`}
            aria-labelledby={`module-${moduleId}`}
        >
            {objectives.map((objective) => {
                const objectiveIndex = getResourceIndex(
                    objective,
                    data,
                    "objective",
                    {
                        disableIndexingByCode: disableIndexingByCode,
                        parentId: moduleId,
                    }
                );
                return (
                    <li
                        key={objective.id}
                        className={cn("accordion-module-list-item__objective", {
                            "accordion-module-list-item__objective--unavailable":
                                objective.visibilityStatus ===
                                VisibilityStatus.Unavailable,
                        })}
                    >
                        {objective.visibilityStatus ===
                        VisibilityStatus.Visible ? (
                            <ObjectiveListItem
                                objectives={objectives}
                                objective={objective}
                                index={objectiveIndex}
                                link={`/player/${moduleId}/${objective.id}`}
                            />
                        ) : (
                            <ObjectiveListItem
                                objectives={objectives}
                                objective={objective}
                                index={objectiveIndex}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

const ToggleButton = ({ expanded }: { expanded: boolean }) => {
    const intl = useIntl();
    if (expanded)
        return (
            <span className="module-objectives-filtered-away__toggle-btn">
                <Icon path="eye_closed" />
                {intl.formatMessage(messages.hide)}
            </span>
        );
    return (
        <span className="module-objectives-filtered-away__toggle-btn">
            <Icon path="eye" />
            {intl.formatMessage(messages.show)}
        </span>
    );
};

const messages = defineMessages({
    a11yUnfoldModuleObjectives: {
        id: "teacher-module-list-item-a11y-unfoldModuleObjectives",
        defaultMessage: "Unfold objective list",
    },
    outOfSpecimen: {
        id: "specimen-out-of-specimen",
        defaultMessage: "(out of specimen)",
    },
    nbFilteredResults: {
        id: "teacherModuleList-nbOfFilteredResults",
        defaultMessage:
            "{count,plural, =0 {No results} 1 {1 result}  other {# results}}",
    },
    nbObjNotMatching: {
        id: "teacherModuleList-nbObjectivesNotMatching",
        defaultMessage:
            "{count,plural,=0{No other objectives} 1{1 other objective} other{# other objectives}} don't match your search",
    },
    hide: {
        id: "teacherModuleList-hideFilteredObjectives",
        defaultMessage: "Hide",
    },
    show: {
        id: "teacherModuleList-showFilteredObjectives",
        defaultMessage: "Show",
    },
    unavailable: {
        id: "teacherModuleList-unavailable",
        defaultMessage: "Unavailable",
    },
});
